import { Injectable } from '@angular/core';
import * as firebase from 'firebase/compat/app';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import { Timestamp } from '@angular/fire/firestore';
import {Observable} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LogService } from './log.service';
import { environment } from '../../environments/environment';
import { Customer, SubscriptionCode, TeamType } from 'app/shared/model/customer';

@Injectable()
export class AuthenticationService {

    private claims: any;
    private _customer: Customer;

    constructor(
      public afAuth: AngularFireAuth,
      private http: HttpClient,
      private logService: LogService
    ) { }

    getUser(): Observable<firebase.default.User> {
        return this.afAuth.authState;
    }

    get email(): string {
      if (this.claims) {
        return this.claims.email;
      }
      return '';
    }

    get team(): string {
      if (this.claims) {
        return this.claims.team;
      }
      return '';
    }

    get teamType(): TeamType {
      if (this.claims) {
        return this.claims.teamType || 'recipient';
      }
      return 'recipient';
    }

    get isAdmin(): boolean {
      return this.team === 'MYTROVE';
    }

    get isNotifyAllowed(): boolean {
      if (this.claims && this.claims.teamType) {
        return this.claims.teamType === 'lawFirm';
      }
      return false;
    }  

    get isDataExportAllowed(): boolean {
      if (this.claims) {
        return this.claims.data_export_allowed || false;
      }
      return false;
    }

    setClaims(claims: any) {
      this.claims = claims;
    }

    get customer(): Customer {
      return this._customer;
    }

    setCustomer(customer: Customer) {
      this._customer = customer;
    }

    isEmailSentOnStatus(status: string): boolean {
      if (this.teamType === 'recipient') {
        if (this.customer && (this.customer.submissionStatusEmails || []).find(statusEmail => statusEmail.submissionStatus === status)) {
          return true;
        }
      }
      return false;
    }

    async signIn(email: string, password: string): Promise<firebase.default.User> {
        return this.afAuth.signInWithEmailAndPassword(email, password).then(credentials => {
          this.logService.log(`Sign in as ${credentials.user.email}`);
          return credentials.user;
        }).catch(error => {
          this.logService.error(`Sign in error ${error.code}`);
          if (error.code === 'auth/multi-factor-auth-required') {
            throw error;
          }
          return undefined;
        });
    }

    async signOut() {
      try {
        const user = await this.afAuth.currentUser;
        const idToken = await user.getIdToken();
        this.claims = undefined;
        this._customer = undefined;
        const url = environment.functionsURL + `/userToken/${idToken}`;
        this.logService.log(`Calling ${url} DELETE with param ${idToken}`);
        await this.http.delete(url).toPromise();
        await this.afAuth.signOut();
      } catch (error) {
        this.logService.error(`Sign out error ${error.code}`);
        await this.afAuth.signOut();
      }
    }

    getFirstName(user): string {
      if (user.displayName) {
        const names = user.displayName.split(' ');
        const lastName = names.pop();
        if (names.length > 0) {
          return names[0];
        }
      }
      return '';
    }

    getLastName(user): string {
      if (user.displayName) {
        const names = user.displayName.split(' ');
        return names.pop();
      }
      return '';
    }

    getClaims(user: firebase.default.User): Promise<any> {
      return user.getIdTokenResult(true).then(result => {
        this.logService.log(`Claims: ${JSON.stringify(result.claims, null, 4)}`);
        return result.claims;
      });

    }

    async isKnownUser(email: string): Promise<boolean> {
      const methods = await this.afAuth.fetchSignInMethodsForEmail(email);
      return methods.indexOf('password') >= 0;
    }
    
    /**
     * True when the customer's subscription to the given feature is running now. A subscription
     * dated to start in the future does not grant access yet, and one whose cancelledOn has passed
     * no longer does - so a lapsed firm keeps the plans it has already sent but cannot send more.
     */
    hasCurrentSubscription(code: SubscriptionCode): boolean {
        if (!this.customer) {
            return false;
        }
        const now = Date.now();
        const subscriptions = this.customer.subscriptions || [];
        this.logService.log(`Checking ${subscriptions.length} subscription(s) of ${this.customer.name} for ${code}: ${JSON.stringify(subscriptions, null, 4)}`);
        const current = subscriptions.find(subscription => {
            if (subscription.code !== code || !subscription.startedOn) {
                return false;
            }
            const startedOn = this.asTimestamp(subscription.startedOn).toDate();
            if (startedOn.getTime() > now) {
                this.logService.log(`Subscription to ${code} does not start until ${startedOn}`);
                return false;
            }
            if (subscription.cancelledOn) {
                const cancelledOn = this.asTimestamp(subscription.cancelledOn).toDate();
                if (cancelledOn.getTime() <= now) {
                    this.logService.log(`Subscription to ${code} started on ${startedOn} but was cancelled on ${cancelledOn}`);
                    return false;
                }
                this.logService.log(`Subscription to ${code} started on ${startedOn} and is cancelled from ${cancelledOn}`);
                return true;
            }
            this.logService.log(`Subscription to ${code} started on ${startedOn} and has no cancellation date`);
            return true;
        });
        this.logService.log(`Current subscription to ${code}: ${current ? 'yes' : 'no'}`);
        return current !== undefined;
    }

    /**
     * A timestamp from a client side query is already a Timestamp, but one returned from an api call
     * has been serialised to the form {_seconds: number, _nanoseconds: number}. The customer is
     * currently fetched by api call, so the serialised form is converted back to a Timestamp.
     */
    private asTimestamp(value: any): firebase.default.firestore.Timestamp {
      if (!value || typeof value.toDate === 'function') {
        return value;
      }
      return new Timestamp(value._seconds, value._nanoseconds);
    }

    async createUser(email: string, password: string, displayName: string): Promise<firebase.default.User> {
      let user;
      return this.afAuth.createUserWithEmailAndPassword(email, password).then(credentials => {
        this.logService.log(`User created for ${credentials.user.email}`);
        user = credentials.user;
        return credentials.user.updateProfile({displayName: displayName});
      }).then(() => {
        return user;
      }).catch(error => {
        this.logService.error(`Failed to crate user: ${error.code}`);
        return undefined;
      });
    }

    resetPassword(email: string): Promise<any> {
      return this.afAuth.sendPasswordResetEmail(email);
    }

    async isPwned(password: string): Promise<boolean> {
      try {
        // Encode password as UTF-8
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
        // Compute SHA-1 hash
        const hashBuffer = await crypto.subtle.digest('SHA-1', data);
        // Convert buffer to hex string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

        this.logService.log(`SHA1: ${hashedPassword}`);
        const url = `https://api.pwnedpasswords.com/range/${hashedPassword.toString().substr(0, 5)}`;
        this.logService.log(`Calling ${url}`);
        const possibleMatches = await this.http.get(url, {
          responseType: 'text'
        }).toPromise();
        this.logService.log(`possibleMatches ${possibleMatches}`);
        return possibleMatches.indexOf(hashedPassword.toString().toUpperCase().substr(5)) >= 0;
      } catch (error) {
        this.logService.error(`Failed to check for pwned password: ${JSON.stringify(error, null, 4)}`);
        return false;
      }
    }
}
